import { pageBox, PAPERS } from './paper';
import { buildMirrored, buildClassic, buildDouble, buildConsolation } from './builders';

// Smart layout engine: picks the largest font size whose bracket geometry
// fits every page viewport, then spends leftover space on wider cards,
// roomier connectors and taller rows. Falls back to uniform vector scaling.
const FS_RANGE = { letter: [14, 5], tabloid: [16, 5] };
const BUILDERS = { mirrored: buildMirrored, classic: buildClassic, double: buildDouble, consolation: buildConsolation };

export function metricsFor(fs, showMeta, ratio = 9) {
  const rowH = fs * 1.75;
  const metaH = showMeta ? fs * 1.4 : 0;
  const cardH = metaH + rowH * 2;
  const hfs = Math.min(10, Math.max(4.5, fs * 0.8));
  const cardW = fs * ratio;
  return {
    fs, rowH, metaH, cardH, showMeta, hfs, headerH: hfs * 2.2, cardW,
    colGap: Math.max(6, fs * 1.2), slotH: cardH + Math.max(2.5, fs * 0.45),
    maxW: Math.max(cardW, Math.min(fs * 18, 250)), maxGap: Math.max(6, fs * 3),
  };
}

function grow(M, key, hi, build, ok) {
  let lo = M[key];
  if (hi <= lo) return M;
  if (ok(build({ ...M, [key]: hi }))) return { ...M, [key]: hi };
  let top = hi;
  for (let i = 0; i < 10; i += 1) {
    const mid = (lo + top) / 2;
    if (ok(build({ ...M, [key]: mid }))) lo = mid;
    else top = mid;
  }
  return { ...M, [key]: lo };
}

function fit(build, area, [fsMax, fsMin], showMeta, ratio) {
  const ok = (gm) => gm.viewports.every((v) => v.w <= area.w + 0.5 && v.h <= area.h + 0.5);
  let M = null;
  for (let fs = fsMax; fs >= fsMin - 1e-6; fs -= 0.25) {
    const m = metricsFor(fs, showMeta, ratio);
    if (ok(build(m))) { M = m; break; }
  }
  if (M) {
    M = grow(M, 'cardW', M.maxW, build, ok);
    M = grow(M, 'colGap', M.maxGap, build, ok);
    M = grow(M, 'slotH', M.cardH * 2.6, build, ok);
  } else M = metricsFor(fsMin, showMeta, ratio);
  const geom = build(M);
  const scale = ok(geom) ? 1 : Math.min(...geom.viewports.map((v) => Math.min(area.w / v.w, area.h / v.h)));
  return { geom, M, scale, effFs: M.fs * scale };
}

export function resolveMode(model, layout) {
  if (model.kind === 'double') return 'double';
  if (model.kind === 'consolation') return 'consolation';
  return layout === 'mirrored' && model.canMirror ? 'mirrored' : 'classic';
}

export function defaultSettings(model) {
  const mirror = model?.kind === 'single' && model.canMirror && model.rounds >= 3;
  return { layout: mirror ? 'mirrored' : 'classic', paper: 'letter', orientation: 'landscape', pages: 1, details: 'auto' };
}

export function computeLayout(model, s, { ratio = 9 } = {}) {
  const page = pageBox(s.paper, s.orientation);
  const titleH = page.paper === 'tabloid' ? 46 : 36;
  const footerH = 12;
  const area = { x: page.margin, y: page.margin + titleH, w: page.w - 2 * page.margin, h: page.h - 2 * page.margin - titleH - footerH };
  const mode = resolveMode(model, s.layout);
  const pages = mode === 'consolation' ? 1 : Number(s.pages) === 2 ? 2 : 1;
  const build = (M) => BUILDERS[mode](model, M, pages);
  const run = (meta) => fit(build, area, FS_RANGE[page.paper], meta, ratio);
  let res;
  if (s.details === 'on') res = run(true);
  else if (s.details === 'off') res = run(false);
  else {
    const withMeta = run(true);
    if (withMeta.effFs >= 7.5) res = withMeta;
    else {
      const bare = run(false);
      res = withMeta.scale < 1 || bare.effFs - withMeta.effFs >= 0.75 ? bare : withMeta;
    }
  }
  return { ...res, page, area, titleH, footerH, mode, pageCount: res.geom.viewports.length };
}

export function autoOptimize(model, s, opts) {
  const layouts = model.kind === 'single' && model.canMirror ? ['mirrored', 'classic'] : [s.layout];
  let best = null;
  layouts.forEach((layout) => ['landscape', 'portrait'].forEach((orientation) => {
    const cand = { ...s, layout, orientation, details: 'auto' };
    const L = computeLayout(model, cand, opts);
    const score = L.effFs * (L.mode === 'mirrored' ? 1.25 : 1) + (L.M.showMeta ? 0.6 : 0) + (orientation === 'landscape' ? 0.4 : 0);
    if (!best || score > best.score) best = { score, settings: cand, layout: L };
  }));
  return best;
}

export const describeLayout = (L) => {
  const mode = { mirrored: 'Mirrored', classic: 'Classic', double: 'Double elimination', consolation: '3-game guarantee' }[L.mode];
  return `${mode} · ${PAPERS[L.page.paper].label} ${L.page.orientation} · ${L.effFs.toFixed(1)} pt text`;
};
